/**
 * Mock data for pools, images, policies, and volumes.
 * Temporary until the formal APIs are available.
 */

import type { Pool, ImageCatalogEntry, Policy, Volume, Sandbox } from '@/types';

export const MOCK_POOLS: Pool[] = [
  {
    poolId: 'default',
    displayName: 'Default',
    region: 'us',
    imageIds: ['azlinuxnginxv3'],
  },
];

export const MOCK_IMAGES: ImageCatalogEntry[] = [
  {
    imageId: 'azlinuxnginxv3',
    name: 'Azure Linux with Nginx',
    description:
      'Azure Linux image pre-configured with Nginx reverse proxy and static site hosting',
    os: 'linux',
    latestRevision: 3,
    tags: ['azlinux', 'nginx', 'web'],
    createdAt: '2026-02-15T14:00:00Z',
  },
];

export const MOCK_POLICIES: Policy[] = [
  {
    policyId: 'dev-fast',
    partnerId: 'w365a-portal',
    tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47',
    defaultRetentionPeriod: 'P7D',
    poolIds: ['default'],
    createdAt: '2026-03-01T10:00:00Z',
    modifiedAt: '2026-03-15T08:30:00Z',
    deletedAt: null,
  },
  {
    policyId: 'team-standard',
    partnerId: 'w365a-portal',
    tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47',
    defaultRetentionPeriod: 'P30D',
    poolIds: ['default'],
    createdAt: '2026-02-20T14:00:00Z',
    modifiedAt: '2026-03-10T16:45:00Z',
    deletedAt: null,
  },
  {
    policyId: 'compliance-long',
    partnerId: 'w365a-portal',
    tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47',
    defaultRetentionPeriod: 'P90D',
    poolIds: [],
    createdAt: '2026-01-15T09:00:00Z',
    modifiedAt: '2026-01-15T09:00:00Z',
    deletedAt: null,
  },
];

export const MOCK_SANDBOXES: Sandbox[] = [
  {
    sandboxId: 'a1b2c3d4e5f67890abcdef1234567890ab',
    poolId: 'default',
    sandboxName: 'dev-workspace',
    imageId: 'azlinuxnginxv3',
    shapeName: '2c4g',
    policies: { maxIdleInSeconds: 3600, maxLifetimeInSeconds: 86400 },
    state: 'Running',
    owner: { tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47', objectId: 'mock-oid-0001' },
    createdAt: '2026-03-23T08:00:00Z',
    expiresAt: '2026-03-24T08:00:00Z',
    toolUri: 'https://sandbox.example.com/dev-workspace',
    ports: [{ port: 22, policy: 'Owner', protocol: 'Http' }],
    tags: { team: 'platform', purpose: 'dev' },
  },
  {
    sandboxId: 'b2c3d4e5f6a78901bcdef12345678901bc',
    poolId: 'default',
    sandboxName: 'ci-runner-42',
    imageId: 'azlinuxnginxv3',
    shapeName: '4c8g',
    policies: { maxIdleInSeconds: 1800, maxLifetimeInSeconds: 43200 },
    state: 'Running',
    owner: { tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47', objectId: 'mock-oid-0002' },
    createdAt: '2026-03-23T10:00:00Z',
    expiresAt: '2026-03-23T22:00:00Z',
    tags: { env: 'ci' },
  },
  {
    sandboxId: 'c3d4e5f6a7b89012cdef123456789012cd',
    poolId: 'default',
    sandboxName: 'test-sandbox-old',
    imageId: 'azlinuxnginxv3',
    shapeName: '2c4g',
    policies: { maxIdleInSeconds: 3600, maxLifetimeInSeconds: 86400 },
    state: 'Terminated',
    owner: { tenantId: '72f988bf-86f1-41af-91ab-2d7cd011db47', objectId: 'mock-oid-0001' },
    createdAt: '2026-03-20T09:00:00Z',
    terminationReason: 'MaxLifetimeExceeded',
    tags: {},
  },
];

export const MOCK_VOLUMES: Volume[] = [
  {
    volumeId: '08200001a1b2c3d4e5f6a7b8c9d0e1f2a3b4',
    policyId: 'team-standard',
    state: 'Active',
    region: 'westus3',
    maxSize: '1Gi',
    retentionPeriod: 'P30D',
    createdAt: '2026-03-18T10:00:00Z',
    lastAttachedAt: '2026-03-23T08:15:00Z',
    retentionExpiresAt: null,
    attachedSandboxId: 'a1b2c3d4e5f67890abcdef1234567890ab',
    attachedSandboxName: 'dev-workspace',
    tags: { team: 'platform', purpose: 'dev' },
  },
  {
    volumeId: '08200002b2c3d4e5f6a7b8c9d0e1f2a3b4c5',
    policyId: 'dev-fast',
    state: 'Active',
    region: 'westus3',
    maxSize: '512Mi',
    retentionPeriod: 'P7D',
    createdAt: '2026-03-20T14:30:00Z',
    lastAttachedAt: '2026-03-22T11:00:00Z',
    retentionExpiresAt: null,
    attachedSandboxId: 'b2c3d4e5f6a78901bcdef12345678901bc',
    attachedSandboxName: 'ci-runner-42',
    tags: { env: 'ci' },
  },
  {
    volumeId: '08200003c3d4e5f6a7b8c9d0e1f2a3b4c5d6',
    policyId: 'team-standard',
    state: 'Idle',
    region: 'eastus2',
    maxSize: '1Gi',
    retentionPeriod: 'P30D',
    createdAt: '2026-03-10T09:00:00Z',
    lastAttachedAt: '2026-03-19T17:00:00Z',
    retentionExpiresAt: '2026-04-18T17:00:00Z',
    attachedSandboxId: null,
    attachedSandboxName: null,
    tags: { team: 'platform', purpose: 'staging' },
  },
  {
    volumeId: '08200004d4e5f6a7b8c9d0e1f2a3b4c5d6e7',
    policyId: 'compliance-long',
    state: 'Idle',
    region: 'westus3',
    maxSize: '256Mi',
    retentionPeriod: 'P90D',
    createdAt: '2026-02-01T12:00:00Z',
    lastAttachedAt: '2026-03-05T09:30:00Z',
    retentionExpiresAt: '2026-06-03T09:30:00Z',
    attachedSandboxId: null,
    attachedSandboxName: null,
    tags: {},
  },
  {
    volumeId: '08200005e5f6a7b8c9d0e1f2a3b4c5d6e7f8',
    policyId: 'dev-fast',
    state: 'Active',
    region: 'eastus2',
    maxSize: '1Gi',
    retentionPeriod: 'P7D',
    createdAt: '2026-03-22T16:00:00Z',
    lastAttachedAt: '2026-03-23T10:00:00Z',
    retentionExpiresAt: null,
    attachedSandboxId: null,
    attachedSandboxName: null,
    tags: { team: 'data', experiment: 'model-v3' },
  },
];
